import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import Header from "../components/header";
import Footer from "../components/Footer";
import CheckinEnquiryForm from "../components/Searchbar-DetailsPage";
import { getPropertyById } from "../Functions/FetchProperties";
import { incrementPropertyView } from '../Functions/TrackingFunctions';
import { sendBookingEnquiry } from '../Functions/BookingEnquiry';
import BackToTop from "../components/BackToTop";
import WhatsappButton from "../components/WhatsappButton";
import { Helmet } from "react-helmet-async";
import '../assets/css/PropertyDetails.css';
import '../assets/css/styles.css';

const PropertyDetails = () => {
    const { id } = useParams();
    const [property, setProperty] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [viewTracked, setViewTracked] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [enquirySuccess, setEnquirySuccess] = useState(false);
    const [enquiryMessage, setEnquiryMessage] = useState('');
    const enquiryFormRef = useRef(null);
    const [showGallery, setShowGallery] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!showGallery) return;

            if (e.key === 'Escape') {
                closeGallery();
            } else if (e.key === 'ArrowRight') {
                nextImage();
            } else if (e.key === 'ArrowLeft') {
                prevImage();
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            // Ensure body scroll is restored if component unmounts while gallery is open
            if (showGallery) {
                document.body.style.overflow = 'auto';
            }
        };
    }, [showGallery]);

    const openGallery = (index = 0) => {
        setCurrentImageIndex(index);
        setShowGallery(true);
        // Save current scroll position
        document.body.dataset.scrollY = window.scrollY;
        document.body.style.overflow = 'hidden';
    };

    const closeGallery = () => {
        // Use setTimeout to ensure state update completes before UI changes
        setTimeout(() => {
            document.body.style.overflow = 'auto';
            document.body.style.paddingRight = '0';

            // If we saved a scroll position, restore it
            if (document.body.dataset.scrollY) {
                window.scrollTo(0, parseInt(document.body.dataset.scrollY || '0'));
                delete document.body.dataset.scrollY;
            }
        }, 0);

        setShowGallery(false);
    };

    const nextImage = () => {
        if (!property) return;

        const galleryImages = [
            property.coverImageUrl || (property.images && property.images[0]),
            ...(Array.isArray(property.additionalImagesUrl) ? property.additionalImagesUrl : [])
        ].filter(Boolean);

        setCurrentImageIndex((prevIndex) =>
            prevIndex === galleryImages.length - 1 ? 0 : prevIndex + 1
        );
    };

    const prevImage = () => {
        if (!property) return;

        const galleryImages = [
            property.coverImageUrl || (property.images && property.images[0]),
            ...(Array.isArray(property.additionalImagesUrl) ? property.additionalImagesUrl : [])
        ].filter(Boolean);

        setCurrentImageIndex((prevIndex) =>
            prevIndex === 0 ? galleryImages.length - 1 : prevIndex - 1
        );
    };

    useEffect(() => {
        const fetchPropertyDetails = async () => {
            try {
                setLoading(true);
                const propertyData = await getPropertyById(id);

                if (propertyData) {
                    setProperty(propertyData);

                    if (!viewTracked && id) {
                        await incrementPropertyView(id);
                        setViewTracked(true);

                        sessionStorage.setItem(`property_viewed_${id}`, 'true');
                    }
                } else {
                    setError("Property not found");
                }
            } catch (err) {
                setError("Failed to load property details");
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            const alreadyViewed = sessionStorage.getItem(`property_viewed_${id}`) === 'true';
            if (alreadyViewed) {
                setViewTracked(true);
            }

            fetchPropertyDetails();
        }
    }, [id, viewTracked]);

    const resetEnquiryForm = () => {
        const formElements = document.querySelectorAll('.property-enquiry-form input[type="text"], .property-enquiry-form input[type="email"], .property-enquiry-form input[type="tel"]');
        formElements.forEach(element => {
            element.value = '';
        });

        if (enquiryFormRef.current && typeof enquiryFormRef.current.resetForm === 'function') {
            enquiryFormRef.current.resetForm();
        }
    };

    const toggleDescription = (e) => {
        e.preventDefault();
        setIsDescriptionExpanded(!isDescriptionExpanded);
    };

    if (loading) {
        return (
            <div className="property-details-page">
                <Helmet>
                    <title>Loading Property Details | LSQ Stays</title>
                    <meta name="description" content="Loading luxury property details. Please wait..." />
                </Helmet>
                <Header currentPage="properties" />
                <div className="loading-container">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3">Loading property details...</p>
                </div>
            </div>
        );
    }

    if (error || !property) {
        return (
            <div className="property-details-page">
                <Helmet>
                    <title>Property Not Found | LSQ Stays</title>
                    <meta name="description" content="Sorry, we couldn't find the property you're looking for." />
                </Helmet>
                <Header currentPage="properties" />
                <div className="loading-container">
                    <div className="alert alert-danger" role="alert">
                        {error || "Property not found"}
                    </div>
                </div>
            </div>
        );
    }

    const mainImage = property.coverImageUrl || (property.images && property.images[0]);
    const additionalImages = Array.isArray(property.additionalImagesUrl)
        ? property.additionalImagesUrl
        : [];

    const galleryImages = [mainImage, ...additionalImages].filter(Boolean);
    while (galleryImages.length < 5) {
        galleryImages.push(mainImage || "https://via.placeholder.com/600x400?text=No+Image");
    }

    const ImageGalleryModal = () => {
        if (!showGallery) return null;

        return (
            <div className="image-gallery-modal">
                <div className="gallery-overlay" onClick={closeGallery}></div>
                <div className="gallery-content">
                    <button className="gallery-close" onClick={closeGallery} aria-label="Close gallery">
                        <i className="fas fa-times"></i>
                    </button>

                    <div className="gallery-image-container">
                        <img
                            src={galleryImages[currentImageIndex]}
                            alt={`${property.propertyTitle} - Image ${currentImageIndex + 1}`}
                            className="gallery-image"
                        />

                        <button className="gallery-nav gallery-prev" onClick={prevImage}>
                            <i className="fas fa-chevron-left"></i>
                        </button>

                        <button className="gallery-nav gallery-next" onClick={nextImage}>
                            <i className="fas fa-chevron-right"></i>
                        </button>

                        <div className="gallery-counter">
                            {currentImageIndex + 1} / {galleryImages.length}
                        </div>
                    </div>

                    <div className="gallery-thumbnails">
                        {galleryImages.map((image, index) => (
                            <div
                                key={index}
                                className={`gallery-thumbnail ${index === currentImageIndex ? 'active' : ''}`}
                                onClick={() => setCurrentImageIndex(index)}
                            >
                                <img src={image} alt={`Thumbnail ${index + 1}`} />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="property-details-page" style={{ textAlign: 'left' }}>
            <Helmet>
                <title>{property.propertyTitle} | Luxury Villa by LSQ Stays</title>
                <meta name="description" content={property.description ? `${property.description.substring(0, 160)}...` : `Book ${property.propertyTitle}, a luxury vacation rental in ${property.location}. Featuring ${property.beds || property.bhkCount || ''} bedrooms and amenities for up to ${property.maxOccupancy || 'several'} guests.`} />
                <link rel="canonical" href={`https://www.lsqstays.in/property/${id}`} />
                <meta property="og:image" content={mainImage} />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "Product",
                        "name": property.propertyTitle,
                        "description": property.description ? property.description.replace(/"/g, '\\"').substring(0, 500) : 'Luxury vacation rental',
                        "image": mainImage,
                        "offers": {
                            "@type": "Offer",
                            "price": property.startPrice || '',
                            "priceCurrency": "INR",
                            "availability": "https://schema.org/InStock"
                        },
                        "address": {
                            "@type": "PostalAddress",
                            "addressLocality": property.location || ''
                        },
                        "amenityFeature": property.amenities || [],
                        "provider": {
                            "@type": "Organization",
                            "name": "LSQ Stays",
                            "logo": {
                                "@type": "ImageObject",
                                "url": "https://www.lsqstays.in/assets/Images/logo.png",
                                "width": 112,
                                "height": 112,
                                "contentUrl": "https://www.lsqstays.in/assets/Images/logo.png"
                            }
                        }
                    })}
                </script>
            </Helmet>
            <Header currentPage="properties" />
            <div className="container" style={{ textAlign: 'left', paddingTop: '40px' }}>
                <div className="row">
                    <div className="col-md-8" style={{ textAlign: 'left' }}>
                        <h1 className="villa-title" style={{ textAlign: 'left' }}>{property.propertyTitle}</h1>
                        <div className="location-badge" style={{ textAlign: 'left', display: 'flex', justifyContent: 'flex-start' }}>
                            <i className="fas fa-map-marker-alt me-2" /> {property.location}
                            {property.mapLink && (
                                <a href={property.mapLink} target="_blank" rel="noopener noreferrer" className="ms-3 text-primary">
                                    <i className="fas fa-eye" /> View On Map
                                </a>
                            )}
                        </div>
                    </div>
                    <div className="col-md-4">
                        <ul className="nav nav-pills justify-content-start justify-content-md-end">
                            <li className="nav-item">
                                <a className="nav-link active" href="#highlights">Property Highlights</a>
                            </li>
                            <li className="nav-item">
                                <a className="nav-link" href="#amenities">Amenities</a>
                            </li>
                            <li className="nav-item">
                                <a className="nav-link" href="#rules">House Rules</a>
                            </li>
                            <li className="nav-item">
                                <a className="nav-link" href="#cancellation">Cancellation Policy</a>
                            </li>
                            <li className="nav-item">
                                <a className="nav-link" href="#payment">Payment Policy</a>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="gallery-container">
                    <div className="row g-2">
                        <div className="col-md-6">
                            <img
                                src={galleryImages[0]}
                                alt={property.propertyTitle}
                                className="main-image"
                                onClick={() => openGallery(0)}
                                style={{ cursor: 'pointer' }}
                            />
                        </div>
                        <div className="col-md-6">
                            <div className="row g-2 mb-2">
                                <div className="col-md-6">
                                    <img
                                        src={galleryImages[1]}
                                        alt={property.propertyTitle}
                                        className="thumbnail"
                                        onClick={() => openGallery(1)}
                                        style={{ cursor: 'pointer' }}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <img
                                        src={galleryImages[2]}
                                        alt={property.propertyTitle}
                                        className="thumbnail"
                                        onClick={() => openGallery(2)}
                                        style={{ cursor: 'pointer' }}
                                    />
                                </div>
                            </div>
                            <div className="row g-2">
                                <div className="col-md-6">
                                    <img
                                        src={galleryImages[3]}
                                        alt={property.propertyTitle}
                                        className="thumbnail"
                                        onClick={() => openGallery(3)}
                                        style={{ cursor: 'pointer' }}
                                    />
                                </div>
                                <div className="col-md-6 position-relative">
                                    <img
                                        src={galleryImages[4]}
                                        alt={property.propertyTitle}
                                        className="thumbnail"
                                        onClick={() => openGallery(4)}
                                        style={{ cursor: 'pointer' }}
                                    />
                                    <div
                                        className="view-all"
                                        onClick={() => openGallery(0)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        View all
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-lg-7 order-2 order-lg-1" style={{ textAlign: 'left' }}>
                        <h2 id="highlights" className="section-title" style={{ textAlign: 'left' }}>About This Villa</h2>
                        <h3 className="mb-4" style={{ textAlign: 'left' }}>Property Highlights</h3>
                        <div className="highlights-container mb-4">
                            {property.propertyHighlights && property.propertyHighlights.length > 0 ? (
                                property.propertyHighlights.map((highlight, index) => (
                                    <div className="highlight-item" key={index} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                        <div className="highlight-icon" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                            <i className="fas fa-check-circle fa-lg" />
                                        </div>
                                        <span>{highlight}</span>
                                    </div>
                                ))
                            ) : (
                                <div className="highlight-item" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                    <div className="highlight-icon" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                        <i className="fas fa-info-circle fa-lg" />
                                    </div>
                                    <span>No highlights available</span>
                                </div>
                            )}
                        </div>

                        {property.description ? (
                            <>
                                <p className="mb-4" style={{ textAlign: 'left' }}>
                                    {property.description.length > 200 && !isDescriptionExpanded
                                        ? `${property.description.substring(0, 200)}...`
                                        : property.description}
                                </p>
                                {property.description.length > 200 && (
                                    <a href="#" className="text-primary d-flex align-items-center mb-4"
                                        style={{ textAlign: 'left', justifyContent: 'flex-start' }}
                                        onClick={toggleDescription}>
                                        {isDescriptionExpanded ? 'See less ' : 'See more '}
                                        <i className={`fas fa-chevron-${isDescriptionExpanded ? 'up' : 'down'} ms-2`} />
                                    </a>
                                )}
                            </>
                        ) : null}

                        <h3 className="mb-3" style={{ textAlign: 'left' }}>Main Features</h3>
                        {property.mainFeatures && property.mainFeatures.length > 0 ? (
                            property.mainFeatures.map((feature, index) => (
                                <div className="feature-item" key={index}>
                                    <i className="fas fa-check-circle feature-icon" />
                                    <span>{feature}</span>
                                </div>
                            ))
                        ) : (
                            <div className="feature-item">
                                <i className="fas fa-info-circle feature-icon" />
                                <span>No features available</span>
                            </div>
                        )}

                        {property.brochureLink && (
                            <a href={property.brochureLink} target="_blank" rel="noopener noreferrer" className="btn btn-outline-primary mt-2 mb-4">
                                <i className="far fa-file-pdf me-2" /> View Villa Brochure
                            </a>
                        )}

                        <h2 id="amenities" className="section-title">Amenities</h2>
                        <div className="row">
                            {property.amenities && property.amenities.length > 0 ? (
                                <>
                                    <div className="col-md-6">
                                        {property.amenities.slice(0, Math.ceil(property.amenities.length / 2)).map((amenity, index) => (
                                            <div className="amenity-item" key={index}>
                                                <i className="fas fa-check-circle amenity-icon" />
                                                <span>{amenity}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="col-md-6">
                                        {property.amenities.slice(Math.ceil(property.amenities.length / 2)).map((amenity, index) => (
                                            <div className="amenity-item" key={index}>
                                                <i className="fas fa-check-circle amenity-icon" />
                                                <span>{amenity}</span>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <div className="col-12">
                                    <div className="amenity-item">
                                        <i className="fas fa-info-circle amenity-icon" />
                                        <span>No amenities available</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        <h2 id="rules" className="section-title">House Rules</h2>
                        <div className="policy-item">
                            <div className="policy-icon">→</div>
                            <p>Relax, unwind Stay Cool! Check-in: 2:00 pm Check-out: 11:00 am.</p>
                        </div>
                        <div className="policy-item">
                            <div className="policy-icon">→</div>
                            <p>Private pool is available from 7:00 am to 10:00 pm.</p>
                        </div>
                        <div className="policy-item">
                            <div className="policy-icon">→</div>
                            <p>No Music is allowed outdoors after 10:00 pm, it can be played indoors at low volume.</p>
                        </div>
                        <div className="policy-item">
                            <div className="policy-icon">→</div>
                            <p>No Food is allowed in the Bedrooms.</p>
                        </div>
                        <div className="policy-item">
                            <div className="policy-icon">→</div>
                            <p>Smoking is strictly prohibited, except for the designated areas in the Villa.</p>
                        </div>
                        <div className="policy-item">
                            <div className="policy-icon">→</div>
                            <div>Recharge your Energy levels in the private pool.</div>
                        </div>
                        <div className="policy-item">
                            <div className="policy-icon">→</div>
                            <div>Indoor Amenities, Outdoor poolside sitting area, beautiful deck.</div>
                        </div>

                        <h2 id="cancellation" className="section-title">Cancellation Policy</h2>
                        <div className="policy-item">
                            <div className="policy-icon">→</div>
                            {/* <div>You can cancel free of charge until 30 days before arrival.</div> */}
                            <div>All advance payments made at the time of booking are strictly non-refundable and non-cancellable, under any circumstances.</div>
                        </div>
                        <div className="policy-item">
                            <div className="policy-icon">→</div>
                            {/* <div>You will be charged the total reservation price if you cancel 30 days before arrival.</div> */}
                            <div>Before completing the payment, customers are required to tick a mandatory checkbox confirming acceptance of this policy and our Terms & Conditions.</div>
                        </div>
                        <div className="policy-item">
                            <div className="policy-icon">→</div>
                            {/* <div>You will be charged the total reservation price if you cancel 30 days before arrival.</div> */}
                            <div>Proceeding with the payment indicates full acceptance of this policy.</div>
                        </div>
                        <div className="policy-item">
                            <div className="policy-icon">→</div>
                            {/* <div>You will be charged the total reservation price if you cancel 30 days before arrival.</div> */}
                            <div>If the customer cancels the booking or chooses not to avail the service, the advance amount will be forfeited and will not be refunded.</div>
                        </div>
                        <div className="policy-item">
                            <div className="policy-icon">→</div>
                            <div>In case of a no-show, you will be charged the total price of the reservation.</div>
                        </div>
                        <div className="policy-item">
                            <div className="policy-icon">→</div>
                            {/* <div>A credit note can be availed which can be redeemed subject to the rates and availability of the Villa.</div> */}
                            <div>In the rare event that the service cannot be delivered due to our fault (e.g., double booking, service unavailability), the advance will be either: Adjusted against a future booking, OR

                                Refunded at our discretion.</div>
                        </div>

                        <h2 id="payment" className="section-title">Payment Policy</h2>
                        <div className="policy-item">
                            <div className="policy-icon">→</div>
                            <div>50% of the total booking amount to be paid at the time of booking.</div>
                        </div>
                        <div className="policy-item">
                            <div className="policy-icon">→</div>
                            <div>The remaining 50% of the total booking amount is to be paid, 7 days before arrival.</div>
                        </div>
                        <div className="policy-item">
                            <div className="policy-icon">→</div>
                            <div>A Refundable Security Deposit is to be given to the Property Manager/Caretaker upon check-in
                                via Cash, Bank Transfer, or UPI.</div>
                        </div>
                        <div className="policy-item">
                            <div className="policy-icon">→</div>
                            <div>The security deposit will be returned at the time of check-out, subject to no spillages,
                                damages and/or breakages, etc, caused by one or more guests between check-in and check-out, an
                                appropriate amount will be deducted from the total security deposit.</div>
                        </div>

                        <div className="info-box mt-4">
                            <div className="d-flex align-items-center">
                                <i className="fas fa-info-circle me-2" />
                                <p className="mb-0">Perfect for Friends &amp; Family, Corporate Retreats</p>
                            </div>
                        </div>
                        <div className="info-box">
                            <div className="d-flex align-items-center">
                                <i className="fas fa-info-circle me-2" />
                                <p className="mb-0">Security Deposit Rs. 10,000/- (Refundable)</p>
                            </div>
                        </div>
                    </div>

                    <div className="col-lg-5 order-1 order-lg-2 mb-4 mb-lg-0">
                        <div className="booking-card">
                            <div className="price-section">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div style={{ textAlign: 'left' }}>
                                        <div className="price-label" style={{ textAlign: 'left' }}>Price Start at</div>
                                        <div className="price-value" style={{ textAlign: 'left' }}>
                                            {property.startPrice ? `₹ ${property.startPrice.toLocaleString()}` : "Contact for pricing"}
                                        </div>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        <p className="villa-details mb-1" style={{ textAlign: 'right' }}>
                                            {property.bhkCount || property.beds ?
                                                (property.propertyType?.toLowerCase().includes('cottage') ?
                                                    `${property.bhkCount || property.beds}` :
                                                    `${property.bhkCount || property.beds} BHK`) :
                                                ""
                                            } {
                                                property.propertyType === "other" && property.customPropertyType
                                                    ? property.customPropertyType.charAt(0).toUpperCase() + property.customPropertyType.slice(1).toLowerCase()
                                                    : property.propertyType
                                                        ? property.propertyType.charAt(0).toUpperCase() + property.propertyType.slice(1).toLowerCase()
                                                        : "Villa"
                                            }
                                        </p>
                                        <p className="villa-details mb-0" style={{ textAlign: 'right' }}>
                                            {property.maxOccupancy ? `${property.maxOccupancy} Guests` : ""}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="form-section">
                                <div className="form-inner-content" style={{ textAlign: 'left' }}>
                                    <h4 className="mb-3" style={{ textAlign: 'left' }}>
                                        <i className="fas fa-comment-dots me-2" /> Best Rates, Just Ask
                                    </h4>

                                    <CheckinEnquiryForm
                                        ref={enquiryFormRef}
                                        isEmbedded={true}
                                        propertyName={property.propertyTitle}
                                        propertyLocation={property.location}
                                        onSubmitEnquiry={async (data, formRef) => {
                                            const enquiryData = {
                                                ...data,
                                                propertyId: id,
                                                propertyName: property.propertyTitle,
                                                location: property.location,
                                                enquiryDate: new Date().toISOString(),
                                                bhkCount: property.bhkCount || 0,
                                                maxOccupancy: property.maxOccupancy || 0,
                                                startPrice: property.startPrice || 0
                                            };

                                            setIsSubmitting(true);
                                            setEnquirySuccess(false);
                                            setEnquiryMessage('Sending your enquiry...');

                                            try {
                                                const dbResult = await sendBookingEnquiry(enquiryData);

                                                if (!dbResult.success) {
                                                    throw new Error(dbResult.error || 'Failed to save enquiry to database');
                                                }

                                                if (formRef && formRef.current) {
                                                    const formData = new FormData(formRef.current);

                                                    // Add booking details explicitly to ensure they're included in the email
                                                    formData.append('_subject', `New Booking Enquiry - ${property.propertyTitle}`);
                                                    formData.append('_template', 'table');
                                                    formData.append('_captcha', 'false');

                                                    // Ensure core contact details are included
                                                    formData.append('name', data.name);
                                                    formData.append('email', data.email);
                                                    formData.append('phone', data.phone);

                                                    // Include booking details
                                                    formData.append('checkInDate', data.checkInDate);
                                                    formData.append('checkOutDate', data.checkOutDate);
                                                    formData.append('adults', data.adults);
                                                    formData.append('children', data.children);
                                                    formData.append('totalGuests', Number(data.adults) + Number(data.children));

                                                    // Include property details
                                                    formData.append('propertyId', id);
                                                    formData.append('propertyTitle', property.propertyTitle);
                                                    formData.append('propertyLocation', property.location);

                                                    const emailResponse = await fetch('https://formsubmit.co/info@lsqstays.com', {
                                                        method: 'POST',
                                                        body: formData,
                                                        headers: {
                                                            'Accept': 'application/json'
                                                        }
                                                    });

                                                    if (!emailResponse.ok) {
                                                        console.warn("Email delivery may have failed:", emailResponse.statusText);
                                                    }
                                                }

                                                setEnquirySuccess(true);
                                                setEnquiryMessage('Thank you for your enquiry! We\'ll get back to you shortly.');
                                                resetEnquiryForm();

                                            } catch (error) {
                                                setEnquirySuccess(false);
                                                setEnquiryMessage('Sorry, there was a problem sending your enquiry. Please try again.');
                                            } finally {
                                                setIsSubmitting(false);
                                            }
                                        }}
                                        isSubmitting={isSubmitting}
                                        className="property-enquiry-form"
                                    />

                                    {enquiryMessage && (
                                        <div className={`alert ${enquirySuccess ? 'alert-success' : 'alert-danger'} mt-3`}>
                                            {enquiryMessage}
                                        </div>
                                    )}

                                    <div className="info-box mt-4">
                                        <p className="mb-0">Looking for the best rate? Submit your enquiry now, and we'll ensure your stay is as
                                            affordable as it is luxurious.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
            <BackToTop />
            <WhatsappButton message={`Hello, I'm interested in booking ${property?.propertyTitle || 'a property'} in ${property?.location || ''}`} />

            <ImageGalleryModal />
        </div>
    );
}

export default PropertyDetails;